import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Markets } from "../Lit/acc-es-data/model";
import { fetchConfig } from "../../common/util/config";
import { setUser } from "../../reducers/boot";
import { store } from "../../store";
import { SetLoginFields } from "../../reducers/model/boot";
import { AUTH_ROUTE_PATHS } from "../../routing/AuthenticationRoutes";
import { normalizeLocale, normalizeSsoOrigin, redirectAuthFailed } from "./impersonation-util";
import { loadEntraAuthConfigs } from "./entra-config";
import { encodeEntraState, ENTRA_CODE_VERIFIER_KEY, ENTRA_STATE_NONCE_KEY } from "./entra-util";
import { generateCodeVerifier, codeChallengeFromVerifier } from "./pkce-util";

/**
 * Entra ID (Azure AD) login initiator.
 *
 * Entra is a public client (PKCE) — no client secret. This generates the PKCE verifier +
 * S256 challenge, stashes the verifier in sessionStorage for the receiver to pick up, and
 * sends the challenge on /authorize. The ABO business identity is carried in the encoded
 * `state` blob (not in token claims), and the shared receiver reads it back on the callback.
 */
export const EntraLogin = () => {
  const [errorText, setErrorText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const locale = normalizeLocale(urlParams.get("locale"));
      const abo = urlParams.get("abo");
      const aff = urlParams.get("aff") || null;
      const entryPage = urlParams.get("entryPage");
      const targetAbo = urlParams.get("targetAbo");
      const startingPeriod = urlParams.get("startingPeriod") || "";
      let ssoOrigin = normalizeSsoOrigin(urlParams.get("ssoOrigin"));

      if (ssoOrigin) {
        const isoCountryCode = ssoOrigin;
        store.dispatch(setUser({ user: { isoCountryCode } }));
      }

      if (aff === null && ssoOrigin === null) {
        setErrorText(
          'Missing both "aff" and "ssoOrigin" params. At least one is required. Redirecting to auth failed page.',
        );
        redirectAuthFailed(navigate);
        return;
      }

      if (!abo || !aff) {
        redirectAuthFailed(navigate);
        return;
      }

      // Pick an ssoOrigin if one was not provided.
      if (ssoOrigin === null) {
        ssoOrigin = new Markets().getByCtry(aff!).iso;
      }

      const config = await fetchConfig(`/configs/${ssoOrigin}.configuration.json`);
      if (config instanceof Error) {
        redirectAuthFailed(navigate);
        return;
      }
      const entra = loadEntraAuthConfigs(config);

      if (locale !== null) {
        SetLoginFields({ fields: { locale: locale } });
      }
      if (entryPage !== null) {
        SetLoginFields({ fields: { entryPage: entryPage } });
      }
      if (startingPeriod) {
        SetLoginFields({ fields: { startingPeriod: startingPeriod } });
      }
      if (targetAbo !== null) {
        SetLoginFields({ fields: { targetAbo: targetAbo } });
      }

      SetLoginFields({
        fields: {
          ssoOrigin: ssoOrigin,
          loginOrigin: "entra",
        },
      });

      // Business identity travels in `state`; the nonce binds the callback to this browser session.
      const nonce = generateCodeVerifier();
      const state = {
        aboNumber: abo,
        salesPlanAff: aff,
        nonce,
        provider: "entra" as const,
      };
      const encodedState = encodeEntraState(state);
      window.sessionStorage.setItem(ENTRA_STATE_NONCE_KEY, nonce);

      const verifier = generateCodeVerifier();
      const challenge = await codeChallengeFromVerifier(verifier);
      window.sessionStorage.setItem(ENTRA_CODE_VERIFIER_KEY, verifier);

      // Local dev override so the loopback redirect resolves to the local receiver route.
      const local = window.location.href.startsWith("http://localhost");
      const localEntraReceiverUrl = "http://localhost:8000/entra-jwt-token.html";
      const redirectUri = local ? localEntraReceiverUrl : entra.RedirectUri;

      // custom scopes have to be prefixed with client id for entra
      const scopes = entra.Scope.map((scope) => `${entra.ClientId}/${scope}`);

      scopes.push(...["profile", "openid"]);

      const authorizeParams = new URLSearchParams({
        client_id: entra.ClientId,
        response_type: "code",
        scope: scopes.join(" "),
        redirect_uri: redirectUri,
        state: encodedState,
        code_challenge: challenge,
        code_challenge_method: "S256",
      });
      const authorizeUrl = `${entra.AuthUrlBase}/authorize?${authorizeParams.toString()}`;

      if (config.maintenanceConfig?.appMaintenance) {
        navigate(AUTH_ROUTE_PATHS.MAINTENANCE);
      } else {
        window.location.replace(authorizeUrl);
      }
    })().catch(() => redirectAuthFailed(navigate));
  }, [navigate]);

  return <h1 style={{ display: "flex", justifyContent: "center", color: "#002f5f" }}>{errorText}</h1>;
};
